import { Action, createReducer, on } from '@ngrx/store';
import { Game } from '../../../games/models/games.interface';
import { GamesActions } from '../../../games/store/actions/actions-type.action';
import { ADD_GAME_ACCOUNT_STEPS } from '../../../profile/models/enums/profile.enum';
import { ProfileGeneralActions } from '../../../profile/store/actions/profileGeneral.action';
import {
    CreateTournamentSteps,
    SetupTournamentSteps,
} from '../../../tournament/models/enums/tournaments.enum';
import { Tournament } from '../../../tournament/models/tournaments.interface';
import { BadResponseWithError } from '../../models/bad-response-with-error.interface';
import { SupervisorSearchItem, PlayerSearchItem } from '../../models/common.interface';
import * as CommonSettingsActions from '../actions/commonSettings.action';
import { ProfileTeam } from '../../../team/models/team.interface';

export interface ItemsAutoSuggest<T> {
    items: T[];
    page: number;
    pageSize: number;
    itemsCount: number;
    total: number;
}

export interface CommonSettingsState {
    sideMenuIsOpen: boolean;
    addGameAccountStep: ADD_GAME_ACCOUNT_STEPS | null;
    addGameAccountSelectedGame: string | null;
    addGameAccountModalIsOpen: boolean;

    playersAutoSuggest: ItemsAutoSuggest<PlayerSearchItem>;
    teamsAutoSuggest: ItemsAutoSuggest<ProfileTeam>;
    isLoading: boolean;
    moderatorsAutoSuggest: ItemsAutoSuggest<SupervisorSearchItem>;
    operatorsAutoSuggest: ItemsAutoSuggest<SupervisorSearchItem>;

    createTournamentModalIsOpen: boolean;
    setupTournamentModalIsOpen: boolean;
    createTournamentCurrentStep: CreateTournamentSteps | null;
    setupTournamentCurrentStep: SetupTournamentSteps | null;
    createTournamentCurrentGame: Game | null;
    createTournamentCurrentGameCode: string | null;
    copyTournament: Tournament | null;

    error: BadResponseWithError | null;
}

const initialState: CommonSettingsState = {
    sideMenuIsOpen: false,

    addGameAccountStep: null,
    addGameAccountSelectedGame: null,
    addGameAccountModalIsOpen: false,

    playersAutoSuggest: {
        items: [],
        page: 0,
        pageSize: 0,
        itemsCount: 0,
        total: 0,
    },
    teamsAutoSuggest: {
        items: [],
        page: 0,
        pageSize: 0,
        itemsCount: 0,
        total: 0,
    },
    moderatorsAutoSuggest: {
        items: [],
        page: 0,
        pageSize: 0,
        itemsCount: 0,
        total: 0,
    },
    operatorsAutoSuggest: {
        items: [],
        page: 0,
        pageSize: 0,
        itemsCount: 0,
        total: 0,
    },
    isLoading: false,

    createTournamentModalIsOpen: false,
    setupTournamentModalIsOpen: false,
    createTournamentCurrentStep: null,
    setupTournamentCurrentStep: null,
    createTournamentCurrentGame: null,
    createTournamentCurrentGameCode: null,
    copyTournament: null,

    error: null,
};

const reducer = createReducer(
    initialState,
    on(CommonSettingsActions.copyTournament, (state, action) => ({
        ...state,
        copyTournament: action.payload.tournament,
    })),
    on(CommonSettingsActions.clearCopyTournament, (state) => ({
        ...state,
        copyTournament: null,
    })),
    on(CommonSettingsActions.openSideMenu, (state) => ({
        ...state,
        sideMenuIsOpen: true,
    })),
    on(CommonSettingsActions.closeSideMenu, (state) => ({
        ...state,
        sideMenuIsOpen: false,
    })),
    on(
        CommonSettingsActions.openCreateTournamentSelectGameModalAction,
        (state) => ({
            ...state,
            createTournamentModalIsOpen: true,
            createTournamentCurrentStep: CreateTournamentSteps.FirstStep,
        })
    ),
    on(
        CommonSettingsActions.closeCreateTournamentSelectGameModalAction,
        (state) => ({
            ...state,
            createTournamentCurrentStep: null,
        })
    ),
    on(CommonSettingsActions.openSetupTournamentModalAction, (state) => ({
        ...state,
        setupTournamentModalIsOpen: true,
        setupTournamentCurrentStep: SetupTournamentSteps.TournamentStep,
    })),
    on(CommonSettingsActions.closeSetupTournamentModalAction, (state) => ({
        ...state,
        setupTournamentCurrentStep: null,
        setupTournamentModalIsOpen: false,
    })),
    on(
        CommonSettingsActions.closeCreateTournamentSecondStepModalAction,
        (state) => ({
            ...state,
            createTournamentModalIsOpen: false,
        })
    ),
    on(GamesActions.selectGame, (state, action) => ({
        ...state,
        createTournamentCurrentGameCode: action.game.name,
    })),
    on(CommonSettingsActions.setSetupTournamentStep, (state, action) => ({
        ...state,
        setupTournamentCurrentStep: action.nextStep,
    })),
    on(ProfileGeneralActions.changePlayerProfileInformation, (state) => ({
        ...state,
        error: null,
    })),
    on(GamesActions.getGameByIdSuccess, (state, action) => {
        const nextState = {
            ...state,
            createTournamentCurrentGame: action.payload.data,
        };
        if (action.payload.openCrateTournamentModal) {
            nextState.createTournamentCurrentStep =
                CreateTournamentSteps.SecondStep;
        }
        if (action.payload.isV2TournamentCreate) {
            nextState.createTournamentCurrentStep =  CreateTournamentSteps.SecondStep;
        }
        return nextState;
    }),
    on(GamesActions.getGameByIdV1Success, (state, action) => {
        const existing = state.createTournamentCurrentGame || ({} as any);
        const enriched = { ...action.payload.data } as any;
        const merged = { ...enriched,platforms: enriched.platforms,
            ...Object.fromEntries(Object.entries(existing).filter(([k,v]) => v !== undefined && v !== null)) } as any;
        const nextState: CommonSettingsState = {
            ...state,
            createTournamentCurrentGame: merged,
            createTournamentCurrentStep: action.payload.openCrateTournamentModal
                ? CreateTournamentSteps.SecondStep
                : state.createTournamentCurrentStep,
        };
        return nextState;
    }),
    on(
        ProfileGeneralActions.changePlayerProfileInformationFailure,
        (state, action) => ({
            ...state,
            error: action.payload,
        })
    ),
    on(
        ProfileGeneralActions.changePlayerProfileInformationSuccess,
        (state) => ({
            ...state,
            error: null,
        })
    ),
    on(ProfileGeneralActions.openAddGameAccountModal, (state) => ({
        ...state,
        addGameAccountModalIsOpen: true,
        addGameAccountStep: ADD_GAME_ACCOUNT_STEPS.FIRST_STEP,
    })),
    on(ProfileGeneralActions.addGameAccountSetGame, (state, action) => ({
        ...state,
        addGameAccountSelectedGame: action.payload,
        addGameAccountStep: ADD_GAME_ACCOUNT_STEPS.SECOND_STEP,
    })),
    on(ProfileGeneralActions.setAddGameAccountStep, (state, action) => ({
        ...state,
        addGameAccountStep: action.payload,
    })),
    on(ProfileGeneralActions.addGameAccountFailure, (state, action) => ({
        ...state,
        error: action.error,
        addGameAccountModalIsOpen: false,
    })),
    on(CommonSettingsActions.searchForPlayers, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(CommonSettingsActions.clearPlayersSearch, (state) => ({
        ...state,
        playersAutoSuggest: initialState.playersAutoSuggest,
        isLoading: false,
    })),
    on(CommonSettingsActions.searchForPlayersSuccess, (state, action) => {
        const { items, ...rest } = action.payload.response;

        return {
            ...state,
            playersAutoSuggest: {
                ...state.playersAutoSuggest,
                ...rest,
                items: action.payload.patchData
                    ? [...state.playersAutoSuggest.items, ...items]
                    : items,
            },
            isLoading: false,
        };
    }),
    on(CommonSettingsActions.searchForPlayersFailure, (state, action) => ({
        ...state,
        playersAutoSuggest: initialState.playersAutoSuggest,
        error: action.payload,
        isLoading: false,
    })),
    on(CommonSettingsActions.searchForTeams, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(CommonSettingsActions.clearTeamsSearch, (state) => ({
        ...state,
        teamsAutoSuggest: initialState.teamsAutoSuggest,
        isLoading: false,
    })),
    on(CommonSettingsActions.searchForTeamsSuccess, (state, action) => {
        const { items, ...rest } = action.payload.response;

        return {
            ...state,
            teamsAutoSuggest: {
                ...state.teamsAutoSuggest,
                ...rest,
                items: action.payload.patchData
                    ? [...state.teamsAutoSuggest.items, ...items]
                    : items,
            },
            isLoading: false,
        };
    }),
    on(CommonSettingsActions.searchForTeamsFailure, (state, action) => ({
        ...state,
        teamsAutoSuggest: initialState.teamsAutoSuggest,
        error: action.payload,
        isLoading: false,
    })),
    on(CommonSettingsActions.searchForModeratorsSuccess, (state, action) => {
        return {
            ...state,
            moderatorsAutoSuggest: {
                ...state.moderatorsAutoSuggest,
                items: action.payload.response,
            },
            isLoading: false,
        };
    }),
    on(CommonSettingsActions.searchForOperatorsSuccess, (state, action) => {
        return {
            ...state,
            operatorsAutoSuggest: {
                ...state.operatorsAutoSuggest,
                items: action.payload.response,
            },
            isLoading: false,
        };
    }),
    on(CommonSettingsActions.openCreateTournamentSelectGameModalActionV2, (state) => ({
        ...state,
        createTournamentModalIsOpen: true,
        createTournamentCurrentStep: CreateTournamentSteps.FirstStep,
    })),
    on(CommonSettingsActions.closeCreateTournamentSelectGameModalActionV2, (state) => ({
        ...state,
        createTournamentCurrentStep: null,
    })),
    on(CommonSettingsActions.openSetupTournamentModalActionV2, (state) => ({
        ...state,
        setupTournamentModalIsOpen: true,
        setupTournamentCurrentStep: SetupTournamentSteps.TournamentStep,
    })),
    on(CommonSettingsActions.closeSetupTournamentModalActionV2, (state) => ({
        ...state,
        setupTournamentCurrentStep: null,
        setupTournamentModalIsOpen: false,
    })),
    on(CommonSettingsActions.closeCreateTournamentSecondStepModalActionV2, (state) => ({
        ...state,
        createTournamentModalIsOpen: false,
    })),
    on(CommonSettingsActions.setSetupTournamentStepV2, (state, action) => ({
        ...state,
        setupTournamentCurrentStep: action.nextStep,
    })),
);

export const commonSettingsReducer = (
    state: CommonSettingsState,
    action: Action
) => reducer(state, action);
